import React, { useState, useEffect } from 'react';
import { useParams, useSearchParams } from 'react-router-dom';
import axios from 'axios';
import { Button } from '../components/ui/button';
import { Card, CardContent } from '../components/ui/card';
import { Badge } from '../components/ui/badge';
import { Input } from '../components/ui/input';
import { ShoppingCart, Plus, Minus, Trash2, X } from 'lucide-react';
import { toast, Toaster } from 'react-hot-toast';
import AIWaiterChatVoice from '../components/AIWaiterChatVoice';

const API = process.env.REACT_APP_BACKEND_URL || '';

export default function CustomerOrderPage() {
  const { tableId } = useParams();
  const [searchParams] = useSearchParams();
  const existingOrderId = searchParams.get('existing_order');
  
  const [loading, setLoading] = useState(true);
  const [categories, setCategories] = useState([]);
  const [menuItems, setMenuItems] = useState([]);
  const [filteredItems, setFilteredItems] = useState([]);
  const [selectedCategory, setSelectedCategory] = useState('all');
  const [cart, setCart] = useState([]);
  const [cartOpen, setCartOpen] = useState(false);
  const [tableName, setTableName] = useState('');
  const [restaurantId, setRestaurantId] = useState('');
  const [customerInfo, setCustomerInfo] = useState({ name: '', phone: '' });
  const [showCustomerForm, setShowCustomerForm] = useState(false);
  const [orderType, setOrderType] = useState('dine_in');

  useEffect(() => {
    if (tableId) {
      fetchMenuData();
      fetchTableInfo();
    }
  }, [tableId]);

  useEffect(() => {
    if (existingOrderId) {
      fetchExistingOrder();
    }
  }, [existingOrderId]);

  useEffect(() => {
    if (selectedCategory === 'all') {
      setFilteredItems(menuItems);
    } else {
      setFilteredItems(menuItems.filter(item => item.category_id === selectedCategory));
    }
  }, [selectedCategory, menuItems]);

  const fetchTableInfo = async () => {
    try {
      const response = await axios.get(`${API}/api/public/tables/${tableId}`);
      setTableName(response.data.table_name || `Table ${tableId.slice(0, 6)}`);
      setRestaurantId(response.data.restaurant_id);
    } catch (error) {
      console.error('Error fetching table info:', error);
      toast.error('Failed to load table information');
      setTableName('Your Table');
    }
  };

  const fetchMenuData = async () => {
    try {
      setLoading(true);
      
      // Fetch categories
      const categoriesResponse = await axios.get(`${API}/api/public/categories?table_id=${tableId}`);
      setCategories(categoriesResponse.data || []);

      // Fetch menu items
      const menuResponse = await axios.get(`${API}/api/public/menu/items?table_id=${tableId}`);
      setMenuItems(menuResponse.data || []);
      setFilteredItems(menuResponse.data || []);
      
    } catch (error) {
      console.error('Error fetching menu:', error);
      toast.error('Failed to load menu. Please try again.');
    } finally {
      setLoading(false);
    }
  };

  const fetchExistingOrder = async () => {
    try {
      const response = await axios.get(`${API}/api/public/orders/${existingOrderId}`);
      const existingOrder = response.data;
      
      if (existingOrder && existingOrder.items) {
        setCart(existingOrder.items.map(item => ({
          ...item,
          quantity: item.quantity || 1
        })));
      }
    } catch (error) {
      console.error('Error fetching existing order:', error);
    }
  };

  const addToCart = (item) => {
    const existingItem = cart.find(cartItem => cartItem.item_id === item.id);
    
    if (existingItem) {
      setCart(cart.map(cartItem =>
        cartItem.item_id === item.id
          ? { ...cartItem, quantity: cartItem.quantity + 1 }
          : cartItem
      ));
    } else {
      setCart([...cart, {
        item_id: item.id,
        item_name: item.item_name || item.name,
        price: item.pricing?.dine_in || item.price || 0,
        quantity: 1,
        notes: ''
      }]);
    }
    
    toast.success(`${item.item_name || item.name} added to cart`);
  };

  const updateQuantity = (itemId, delta) => {
    setCart(cart.map(item => {
      if (item.item_id === itemId) {
        const newQuantity = item.quantity + delta;
        return newQuantity > 0 ? { ...item, quantity: newQuantity } : item;
      }
      return item;
    }).filter(item => item.quantity > 0));
  };

  const removeFromCart = (itemId) => {
    setCart(cart.filter(item => item.item_id !== itemId));
    toast.success('Item removed from cart');
  };

  const calculateTotal = () => {
    return cart.reduce((total, item) => total + (item.price * item.quantity), 0);
  };

  const handlePlaceOrder = async () => {
    if (cart.length === 0) {
      toast.error('Your cart is empty');
      return;
    }

    // For online orders without table, ask for customer info
    if (!tableId && (!customerInfo.name || !customerInfo.phone)) {
      setShowCustomerForm(true);
      return;
    }

    try {
      const orderData = {
        table_id: tableId || null,
        order_type: tableId ? 'dine_in' : 'online',
        items: cart,
        customer_name: customerInfo.name || 'Walk-in Customer',
        customer_phone: customerInfo.phone || '',
        grand_total: calculateTotal(),
        notes: ''
      };

      const response = await axios.post(`${API}/api/orders/customer`, orderData);
      
      if (response.status === 200 || response.status === 201) {
        toast.success('Order placed successfully!');
        setCart([]);
        setCartOpen(false);
        setShowCustomerForm(false);
      }
    } catch (error) {
      console.error('Error placing order:', error);
      toast.error('Failed to place order. Please try again.');
    }
  };

  const handleOrderComplete = (orderData) => {
    toast.success('Voice order placed successfully!');
    setCart([]);
    setCartOpen(false);
  };

  if (loading) {
    return (
      <div className="min-h-screen bg-gray-50 flex items-center justify-center">
        <div className="text-center">
          <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-blue-600 mx-auto"></div>
          <p className="mt-4 text-gray-600">Loading menu...</p>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gray-50 pb-24">
      <Toaster position="top-center" />
      
      {/* Header */}
      <div className="sticky top-0 z-40 bg-white shadow-sm">
        <div className="max-w-2xl mx-auto px-4 py-4">
          <h1 className="text-2xl font-bold text-gray-800">WaiterMan POS</h1>
          <p className="text-sm text-gray-500">{tableName}</p>
        </div>
      </div>

      {/* Categories */}
      <div className="sticky top-[72px] z-30 bg-gray-50 py-4">
        <div className="max-w-2xl mx-auto px-4 overflow-x-auto">
          <div className="flex gap-2 whitespace-nowrap">
            <Button
              variant={selectedCategory === 'all' ? 'default' : 'outline'}
              onClick={() => setSelectedCategory('all')}
              size="sm"
            >
              All Items
            </Button>
            {categories.map(category => (
              <Button
                key={category.id}
                variant={selectedCategory === category.id ? 'default' : 'outline'}
                onClick={() => setSelectedCategory(category.id)}
                size="sm"
              >
                {category.name}
              </Button>
            ))}
          </div>
        </div>
      </div>

      {/* Menu Items */}
      <div className="max-w-2xl mx-auto px-4 py-6">
        <div className="grid gap-4">
          {filteredItems.length === 0 ? (
            <div className="text-center py-12 text-gray-400">
              <p>No items available</p>
            </div>
          ) : (
            filteredItems.map(item => (
              <Card key={item.id} className="hover:shadow-md transition-shadow">
                <CardContent className="p-4">
                  <div className="flex justify-between items-start">
                    <div className="flex-1">
                      <h3 className="font-semibold text-gray-800">
                        {item.item_name || item.name}
                      </h3>
                      {item.description && (
                        <p className="text-sm text-gray-500 mt-1">{item.description}</p>
                      )}
                      <div className="flex items-center gap-2 mt-2">
                        <span className="text-lg font-bold text-blue-600">
                          ${(item.pricing?.dine_in || item.price || 0).toFixed(2)}
                        </span>
                        {item.available !== false && (
                          <Badge variant="outline" className="text-green-600 border-green-600">
                            Available
                          </Badge>
                        )}
                      </div>
                    </div>
                    <Button
                      onClick={() => addToCart(item)}
                      disabled={item.available === false}
                      className="ml-4"
                    >
                      <Plus className="h-4 w-4" />
                    </Button>
                  </div>
                </CardContent>
              </Card>
            ))
          )}
        </div>
      </div>

      {/* Cart Button */}
      {cart.length > 0 && (
        <div className="fixed bottom-20 right-4 z-40">
          <Button
            onClick={() => setCartOpen(true)}
            className="rounded-full h-14 w-14 shadow-lg"
          >
            <div className="relative">
              <ShoppingCart className="h-6 w-6" />
              <Badge className="absolute -top-2 -right-2 h-5 w-5 flex items-center justify-center p-0">
                {cart.length}
              </Badge>
            </div>
          </Button>
        </div>
      )}

      {/* Voice AI Waiter */}
      <AIWaiterChatVoice
        tableId={tableId}
        restaurantId={restaurantId}
        onOrderComplete={handleOrderComplete}
      />

      {/* Cart Modal */}
      {cartOpen && (
        <div className="fixed inset-0 bg-black bg-opacity-50 z-50 flex items-end">
          <div className="bg-white w-full max-h-[80vh] rounded-t-3xl overflow-hidden">
            <div className="p-4 border-b sticky top-0 bg-white">
              <div className="flex justify-between items-center">
                <h2 className="text-xl font-bold">Your Order</h2>
                <Button variant="ghost" size="sm" onClick={() => setCartOpen(false)}>
                  <X className="h-5 w-5" />
                </Button>
              </div>
            </div>

            <div className="p-4 overflow-y-auto max-h-[50vh]">
              {cart.map(item => (
                <div key={item.item_id} className="flex items-center gap-3 py-3 border-b">
                  <div className="flex-1">
                    <p className="font-medium">{item.item_name}</p>
                    <p className="text-sm text-gray-500">${item.price.toFixed(2)} each</p>
                  </div>
                  <div className="flex items-center gap-2">
                    <Button
                      variant="outline"
                      size="sm"
                      onClick={() => updateQuantity(item.item_id, -1)}
                    >
                      <Minus className="h-3 w-3" />
                    </Button>
                    <span className="w-8 text-center font-medium">{item.quantity}</span>
                    <Button
                      variant="outline"
                      size="sm"
                      onClick={() => updateQuantity(item.item_id, 1)}
                    >
                      <Plus className="h-3 w-3" />
                    </Button>
                    <Button
                      variant="ghost"
                      size="sm"
                      onClick={() => removeFromCart(item.item_id)}
                      className="text-red-600"
                    >
                      <Trash2 className="h-4 w-4" />
                    </Button>
                  </div>
                </div>
              ))}
            </div>

            {showCustomerForm && (
              <div className="p-4 border-t bg-gray-50">
                <h3 className="font-semibold mb-3">Customer Information</h3>
                <Input
                  placeholder="Your Name"
                  value={customerInfo.name}
                  onChange={(e) => setCustomerInfo({ ...customerInfo, name: e.target.value })}
                  className="mb-2"
                />
                <Input
                  placeholder="Phone Number"
                  value={customerInfo.phone}
                  onChange={(e) => setCustomerInfo({ ...customerInfo, phone: e.target.value })}
                />
              </div>
            )}

            <div className="p-4 border-t bg-white">
              <div className="flex justify-between items-center mb-4">
                <span className="text-lg font-semibold">Total:</span>
                <span className="text-2xl font-bold text-blue-600">
                  ${calculateTotal().toFixed(2)}
                </span>
              </div>
              <Button
                onClick={handlePlaceOrder}
                className="w-full"
                size="lg"
              >
                Place Order
              </Button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
